import { useState, useCallback, useEffect } from 'react';
import styles from './entity-preview-card.module.css';
import { Flex, Text, IconButton, Skeleton, Icon } from '@nula/core-ui';
import { useDocumentViewerRenderer } from '@/lib/documents/document-viewer-context';
import { useCanvasPreviewRenderer } from '@/lib/canvas/canvas-preview-context';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import type { DocumentPreviewData } from '@/hooks/use-entity-preview';

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat(navigator.language, { month: 'short', day: 'numeric' }).format(date);
}

interface EntityPreviewCardProps {
  data: DocumentPreviewData | null;
  loading?: boolean;
  onOpen?: (entityId: string) => void;
}

export function EntityPreviewCard({ data, loading, onOpen }: EntityPreviewCardProps) {
  const DocumentViewer = useDocumentViewerRenderer();
  const CanvasPreviewRenderer = useCanvasPreviewRenderer();
  // Document + canvas renderers are provided via context by the host
  // app (see lib/documents/document-viewer-context + lib/canvas/canvas-
  // preview-context, both mounted in app/router.tsx). When neither
  // provider is mounted the card falls back to a skeleton placeholder
  // — this keeps shared tests + non-editor surfaces working.
  const isCanvas = data?.kind === 'canvas' && CanvasPreviewRenderer !== null;

  const [wordCount, setWordCount] = useState(0);
  const [nodeCount, setNodeCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  const handleDocStats = useCallback((stats: { wordCount: number }) => setWordCount(stats.wordCount), []);
  const handleCanvasStats = useCallback((stats: { nodeCount: number }) => setNodeCount(stats.nodeCount), []);

  // Canvas is ready immediately — CanvasViewerPreview handles its own loading state
  const ready = isCanvas || wordCount > 0 || timedOut;

  // Fallback: stop showing skeleton after 3s even if Yjs content never syncs
  useEffect(() => {
    if (isCanvas) return;
    const timer = setTimeout(() => setTimedOut(true), 3000);
    return () => clearTimeout(timer);
  }, [isCanvas]);

  if (loading || !data || !ready) {
    return (
      <Flex vertical gap="sm" className={styles.loadingContainer}>
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 3 }} />
        {/* Mount DocumentViewer offscreen to start loading */}
        {data && !isCanvas && DocumentViewer && (
          <Flex className={styles.hiddenLoader}>
            <DocumentViewer entityId={data.id} onStats={handleDocStats} />
          </Flex>
        )}
      </Flex>
    );
  }

  const readMin = Math.max(1, Math.round(wordCount / 200));
  const titleIcon = getEntityKindMeta(data.kind).icon;
  // Canvas needs fixed height so CanvasViewerPreview fills it;
  // documents shrink to content capped by max-height.
  const containerClass = isCanvas ? styles.canvasContainer : styles.container;

  // Footer right-hand stat: node count for canvas, word count for documents
  const statLabel = isCanvas ? `${nodeCount} nodes` : `${wordCount} words`;

  return (
    <Flex vertical gap="sm" className={containerClass}>
      {/* Title + open button */}
      <Flex align="center" gap="sm" className={styles.noShrink}>
        <Icon name={titleIcon} size={14} aria-hidden="true" className={styles.titleIcon} />
        <Text size="sm" strong ellipsis className={styles.titleText}>
          {data.title || 'Untitled'}
        </Text>
        {onOpen && (
          <IconButton
            size="sm"
            aria-label="Open document"
            onClick={() => onOpen(data.id)}
            className={styles.openButton}
          >
            <Icon name="open_in_new" size={12} />
          </IconButton>
        )}
      </Flex>

      {/* Content area */}
      <Flex vertical className={styles.contentArea}>
        {isCanvas && CanvasPreviewRenderer ? (
          <CanvasPreviewRenderer entityId={data.id} onStats={handleCanvasStats} />
        ) : DocumentViewer ? (
          <DocumentViewer entityId={data.id} onStats={handleDocStats} />
        ) : (
          <Skeleton active paragraph={{ rows: 3 }} />
        )}
      </Flex>

      {/* Meta row — same structure for both kinds */}
      <Flex align="center" gap="sm" className={styles.noShrink}>
        <Icon name="history" size={10} aria-hidden="true" className={styles.metaIcon} />
        {!isCanvas && (
          <>
            <Text size="xs" type="secondary" className={styles.tabularNums}>{readMin} min read</Text>
            <Text size="xs" type="secondary">·</Text>
          </>
        )}
        <Text size="xs" type="secondary">{formatDate(data.updatedAt)}</Text>
        <Text size="xs" type="secondary">·</Text>
        <Text size="xs" type="secondary" className={styles.tabularNums}>{statLabel}</Text>
      </Flex>
    </Flex>
  );
}
